import {Component, OnInit} from '@angular/core';
import {environment} from "@biletix/env";
import {RouterService} from "@biletix/core/services/router.service";
import {RegionService} from "@biletix/core/services/region.service";
import {I18nService} from "@biletix/core/services/i18n.service";
import {AbstractionComponent} from "@biletix/core/common/abstraction-component";

export interface EventCategoryItem {
    categoryName: string;
    linkLabel?: string;
    value: string | any;
    icon?: string;
    url?: string;
    children?: EventCategoryItem[]
}


@Component({
    standalone: false,
    selector: 'btx-event-category-menu',
    templateUrl: './event-category-menu.component.html',
    styleUrls: ['./event-category-menu.component.scss']
})
export class EventCategoryMenuComponent extends AbstractionComponent implements OnInit {

    categories: EventCategoryItem[] = [];
    host!: string;

    regionAndLanguagePath: string | any;

    categoryMenuExpanded: boolean = false;

    constructor(private routerService: RouterService,
                private regionService: RegionService,
                private i18n: I18nService) {

        super();
    }

    ngOnInit() {
        //
        this.initializeVariables();
        //
        this.onRegionChangeSubscription();

        //
        this.onLanguageChangeSubscription();
    }

    initializeVariables() {
        this.host = this.routerService.getDomain();
        this.regionAndLanguagePath = this.routerService.regionLanguagePath;
        this.categories = this.createEventCategories();
    }

    onRegionChangeSubscription() {
        this.regionService.regionChange
            .pipe(this.takeUntilDestroy())
            .subscribe(() => this.initializeVariables())
    }

    onLanguageChangeSubscription() {
        this.i18n.changeLangEvent
            .pipe(this.takeUntilDestroy())
            .subscribe(() => this.initializeVariables())
    }

    getFootballUrl(){
        const footballUrl = `${environment.baseHref}football/${this.regionAndLanguagePath}`;
        return footballUrl;
    }

    /**
     * TODO: Etkinlik Kategori ve sub categorileri servisten alınmalıdır.
     *
     * Mobile için de kullanılabilir. Static olmamalıdır.
     */
    createEventCategories(): EventCategoryItem[] {
        //TODO: Anasayfa ekran tasarımı yapıldığında burası düzenlenecek..
        // https://www.biletix.com/category/MUSIC/TURKIYE/tr
        // https://www.biletix.com/search/TURKIYE/tr#subcategory:pop$MUSIC&qt=standard

        const categories: EventCategoryItem[] = [
            //Music
            {
                categoryName: 'main.category.music',
                linkLabel: 'main.category.seeAllMusicEvents',
                value: 'MUSIC',
                url: `${this.host}/category/MUSIC/${this.regionAndLanguagePath}`,
                children: [
                    {
                        categoryName: 'main.subcategory.music.pop',
                        value: 'pop',
                        icon: 'assets/images/category/pop.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:pop$MUSIC&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.music.rock',
                        value: 'rock',
                        icon: 'assets/images/category/rock.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:rock$MUSIC&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.music.jazz',
                        value: 'jazz',
                        icon: 'assets/images/category/jazz.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:jazz$MUSIC&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.music.classical',
                        value: 'klasik',
                        icon: 'assets/images/category/classical.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:klasik$MUSIC&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.music.alternative',
                        value: 'alternative',
                        icon: 'assets/images/category/alternative.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:alternatif$MUSIC&qt=standard`
                    }

                ]

            },
            //Stage
            {
                categoryName: 'main.category.stage',
                linkLabel: 'main.category.seeAllArtEvents',
                value: 'ART',
                url: `${this.host}/category/ART/${this.regionAndLanguagePath}`,
                children: [
                    {
                        categoryName: 'main.subcategory.stage.theater',
                        value: 'tiyatro',
                        icon: 'assets/images/category/theatre.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:tiyatro$ART&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.stage.show',
                        value: 'gosteri',
                        icon: 'assets/images/category/show.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:gosteri$ART&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.stage.dance',
                        value: 'bale_dans',
                        icon: 'assets/images/category/dance.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:bale_dans$ART&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.stage.standUp',
                        value: 'stand_up',
                        icon: 'assets/images/category/standup.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:stand_up$ART&qt=standard`
                    }

                ]
            },
            //Sport
            {
                categoryName: 'main.category.sport',
                linkLabel: 'main.category.seeAllSportEvents',
                value: 'SPORT',
                url: `${this.host}/category/SPORT/${this.regionAndLanguagePath}`,
                children: [
                    {
                        categoryName: 'main.subcategory.sport.football',
                        value: 'futbol',
                        icon: 'assets/images/category/football.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:futbol$SPORT&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.sport.basketball',
                        value: 'basketbol',
                        icon: 'assets/images/category/basketball.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:basketbol$SPORT&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.sport.motorsports',
                        value: 'motorspor',
                        icon: 'assets/images/category/motorsports.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:motorspor$SPORT&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.sport.volleyball',
                        value: 'voleybol',
                        icon: 'assets/images/category/volleyball.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:voleybol$SPORT&qt=standard`
                    }

                ]

            },
            //Family
            {
                categoryName: 'main.category.family',
                linkLabel: 'main.category.seeAllFamilyEvents',
                value: 'FAMILY',
                url: `${this.host}/category/FAMILY/${this.regionAndLanguagePath}`,
                children: [
                    {
                        categoryName: 'main.subcategory.family.show',
                        value: 'gosteri',
                        icon: 'assets/images/category/familyshow.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:gosteri$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.circus',
                        value: 'sirk',
                        icon: 'assets/images/category/circus.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:sirk$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.theater',
                        value: 'tiyatro',
                        icon: 'assets/images/category/familytheatre.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:tiyatro$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.musical',
                        value: 'musical',
                        icon: 'assets/images/category/musical.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:musical$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.zoo',
                        value: 'zoo',
                        icon: 'assets/images/category/zoo.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:zoo$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.themePark',
                        value: 'themepark',
                        icon: 'assets/images/category/themepark.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:themepark$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.family.attractionCenter',
                        value: 'attractioncenter',
                        icon: 'assets/images/category/attractioncenter.png',
                        url: `${this.host}/search/${this.regionAndLanguagePath}#subcategory:attractioncenter$FAMILY&qt=standard`
                    },
                    {
                        categoryName: 'main.subcategory.education.familyChildren',
                        value: 'familyChildren',
                        icon: 'assets/images/category/family.png',
                        url: `https://guides.biletix.com/ailevecocuk/${this.regionAndLanguagePath}`
                    }


                ]

            },

            //Education & More
            {
                categoryName: 'main.category.education',
                linkLabel: 'main.category.education',
                value: 'EDUCATION',
                url: `${this.host}/category/OTHER/${this.regionAndLanguagePath}`,
                children: [
                    {
                        categoryName: 'main.subcategory.education.museum',
                        value: 'museum',
                        icon: 'assets/images/category/museum.png',
                        url: `https://guides.biletix.com/muze-sergi/${this.regionAndLanguagePath}`
                    },
                    // {
                    //     categoryName: 'main.subcategory.education.msa',
                    //     value: 'msa',
                    //     icon: 'assets/images/category/msa.png',
                    //     url: `https://guides.biletix.com/msa/${this.regionAndLanguagePath}`
                    // },
                    {
                        categoryName: 'main.subcategory.education.kulup',
                        value: 'kulup',
                        icon: 'assets/images/category/kulup.png',
                        url: `https://guides.biletix.com/kulup/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.onstage',
                        value: 'tiyatrolar',
                        icon: 'assets/images/category/onstage.png',
                        url: `https://guides.biletix.com/tiyatrolar/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.familyChildren',
                        value: 'familyChildren',
                        icon: 'assets/images/category/family.png',
                        url: `https://guides.biletix.com/ailevecocuk/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.comedy',
                        value: 'comedy',
                        icon: 'assets/images/category/comedy.png',
                        url: `https://guides.biletix.com/komedi/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.raphiphop',
                        value: 'raphiphop',
                        icon: 'assets/images/category/raphiphop.png',
                        url: `https://guides.biletix.com/rap-hiphop/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.acikhava',
                        value: 'acikhava',
                        icon: 'assets/images/category/acikhava.png',
                        url: `https://guides.biletix.com/acikhava/${this.regionAndLanguagePath}`
                    },
                    {
                        categoryName: 'main.subcategory.education.festivaltime',
                        value: 'festivalzamani',
                        icon: 'assets/images/category/festivalzamani.png',
                        url: `https://guides.biletix.com/festivalzamani/${this.regionAndLanguagePath}`
                    }
                ]

            },
        ];
        return categories;

    }

}
